import { api, apiErrorMessage } from './api';
import { asArray, asDefinedArray, asNumber, asString } from './collections';
import { METRIC_KEYS, PLATFORM_IDS, TONES, type MetricKey, type PlatformId, type Tone } from '@ai-media/shared';

/**
 * Typed client for `/assistant/*` — the exact endpoints the MCP server exposes
 * to agents (see `apps/mcp-server/src/tools.ts`). The assistant page and any
 * external agent therefore drive the same code path.
 *
 * All write endpoints are two-phase: the assistant only *proposes*, the user
 * confirms, and only then does the page persist anything.
 */

export type AssistantRange =
  | 'today' | 'yesterday' | 'tomorrow' | 'this_week' | 'last_week' | 'next_week'
  | 'last_7_days' | 'last_30_days' | 'custom';

export interface RangeQuery {
  range?: AssistantRange;
  from?: string;
  to?: string;
  platform?: PlatformId;
  campaignId?: string;
}

export interface Capabilities {
  timezone: string;
  demoMode: boolean;
  simulatedPublish: boolean;
  ai: { provider: string; chain: string[]; model: string | null };
  platforms: Array<{
    id: PlatformId;
    label: string;
    configured: boolean;
    connected: boolean;
    accountName: string | null;
    requiresMedia: boolean;
    maxChars: number;
    recommendedChars: number;
    maxHashtags: number;
  }>;
  missingAccounts: PlatformId[];
  brandVoice: { configured: boolean; tone: Tone; platforms: PlatformId[] };
}

export interface PlatformVariant {
  platformId: PlatformId;
  caption: string;
  charCount: number;
  hashtagCount: number;
  warnings: string[];
  errors: string[];
}

export interface VariationResult extends PlatformVariant {
  hashtags: string[];
  model: string;
  degraded: boolean;
}

export interface PreflightResult {
  ok: boolean;
  demo: boolean;
  errors: Array<{ code: string; message: string; platform?: PlatformId }>;
  warnings: string[];
  timezone: string;
  scheduledAt: string | null;
  scheduledAtLocal: string | null;
  platforms: { ready: PlatformId[]; missingAccounts: PlatformId[]; invalid: string[] };
  content: { valid: boolean; variants: PlatformVariant[] };
}

export interface CampaignProposalItem {
  dayOffset: number;
  publishAt: string;
  publishAtLocal: string;
  platformIds: PlatformId[];
  title: string;
  hook: string;
  body: string;
  hashtags: string[];
  valid: boolean;
  warnings: string[];
}

export interface CampaignProposal {
  requiresConfirmation: true;
  mode: 'live' | 'demo';
  name: string;
  objective: string;
  audience: string;
  platforms: PlatformId[];
  durationDays: number;
  postsPerWeek: number;
  startAt: string;
  timezone: string;
  summary: string;
  cadence: string;
  model: string;
  itemTotal: number;
  items: CampaignProposalItem[];
  warnings: string[];
}

export interface CampaignPlanInput {
  name: string;
  objective?: 'awareness' | 'engagement' | 'leadgen' | 'traffic' | 'sales';
  audience?: string;
  platforms: PlatformId[];
  durationDays?: number;
  postsPerWeek?: number;
  startAt?: string;
  timezone?: string;
  tone?: Tone;
  keywords?: string[];
  language?: string;
}

export type MetricTotals = Record<MetricKey, number>;

export interface AssistantAnalytics {
  range: { label: string; from: string; to: string; timezone: string };
  metrics: MetricTotals;
  engagementRate: number;
  postsPublished: number;
  platformBreakdown: Array<{ platformId: PlatformId; label: string; metrics: MetricTotals }>;
  changes: Record<string, number>;
  headline: string;
  highlights: string[];
  flags: string[];
}

export interface AssistantNarrative {
  range: { label: string; from: string; to: string; timezone: string };
  summary: string;
  insights: string[];
  model: string;
  degraded: boolean;
  metrics: MetricTotals;
  engagementRate: number;
  postsPublished: number;
  simulated: boolean;
}

export interface RankedPost {
  id: string;
  title: string | null;
  platformIds: PlatformId[];
  publishedAt: string | null;
  permalink: string | null;
  metricValue: number;
  engagements: number;
  impressions: number;
}

// ---------------------------------------------------------------------------
// Runtime normalisers
//
// The interfaces above describe the wire contract, but a stale backend build, a
// demo deployment or a partial error payload can still omit a field. Every
// response is normalised here, so arrays are always arrays and numbers always
// numbers before a component calls `.map()` / `.toFixed()` on them.
// ---------------------------------------------------------------------------

function normaliseRange(value?: AssistantAnalytics['range']): AssistantAnalytics['range'] {
  return {
    label: asString(value?.label),
    from: asString(value?.from),
    to: asString(value?.to),
    timezone: asString(value?.timezone),
  };
}

function normaliseMetrics(value?: MetricTotals): MetricTotals {
  const metrics = {} as MetricTotals;
  for (const key of METRIC_KEYS) metrics[key] = asNumber(value?.[key]);
  return metrics;
}

function normaliseMetricKey(value: unknown): MetricKey {
  return (METRIC_KEYS as readonly unknown[]).includes(value) ? (value as MetricKey) : 'engagements';
}

function normaliseCapabilities(data: Capabilities | null | undefined): Capabilities {
  const source = data ?? ({} as Capabilities);
  return {
    ...source,
    timezone: asString(source.timezone),
    demoMode: Boolean(source.demoMode),
    simulatedPublish: Boolean(source.simulatedPublish),
    ai: {
      provider: asString(source.ai?.provider),
      chain: asArray(source.ai?.chain),
      model: source.ai?.model ?? null,
    },
    platforms: asDefinedArray(source.platforms).map((platform) => ({
      ...platform,
      connected: Boolean(platform.connected),
      configured: Boolean(platform.configured),
      accountName: platform.accountName ?? null,
      maxChars: asNumber(platform.maxChars),
      recommendedChars: asNumber(platform.recommendedChars),
      maxHashtags: asNumber(platform.maxHashtags),
    })),
    missingAccounts: asArray(source.missingAccounts),
    brandVoice: {
      configured: Boolean(source.brandVoice?.configured),
      tone: source.brandVoice?.tone ?? 'friendly',
      platforms: asArray(source.brandVoice?.platforms),
    },
  };
}

function normaliseVariant(variant: VariationResult): VariationResult {
  return {
    ...variant,
    caption: asString(variant.caption),
    charCount: asNumber(variant.charCount),
    hashtagCount: asNumber(variant.hashtagCount),
    hashtags: asArray(variant.hashtags),
    warnings: asArray(variant.warnings),
    errors: asArray(variant.errors),
    model: asString(variant.model),
    degraded: Boolean(variant.degraded),
  };
}

function normaliseProposal(proposal: CampaignProposal | null | undefined): CampaignProposal {
  const source = proposal ?? ({} as CampaignProposal);
  return {
    ...source,
    platforms: asArray(source.platforms),
    warnings: asArray(source.warnings),
    itemTotal: asNumber(source.itemTotal),
    durationDays: asNumber(source.durationDays),
    postsPerWeek: asNumber(source.postsPerWeek),
    mode: source.mode === 'demo' ? 'demo' : 'live',
    items: asDefinedArray(source.items).map((item) => {
      const warnings = asArray(item.warnings);
      return {
        ...item,
        platformIds: asArray(item.platformIds),
        hashtags: asArray(item.hashtags),
        warnings,
        valid: item.valid ?? warnings.length === 0,
      };
    }),
  };
}

function normaliseRankedPost(post: RankedPost): RankedPost {
  return {
    ...post,
    title: typeof post.title === 'string' ? post.title : null,
    platformIds: asArray(post.platformIds),
    metricValue: asNumber(post.metricValue),
    engagements: asNumber(post.engagements),
    impressions: asNumber(post.impressions),
  };
}

function normaliseNarrative(data: AssistantNarrative | null | undefined): AssistantNarrative {
  const source = data ?? ({} as AssistantNarrative);
  return {
    ...source,
    range: normaliseRange(source.range),
    summary: asString(source.summary),
    insights: asArray(source.insights),
    model: asString(source.model),
    degraded: Boolean(source.degraded),
    metrics: normaliseMetrics(source.metrics),
    engagementRate: asNumber(source.engagementRate),
    postsPublished: asNumber(source.postsPublished),
    simulated: Boolean(source.simulated),
  };
}

function normaliseAnalytics(data: AssistantAnalytics | null | undefined): AssistantAnalytics {
  const source = data ?? ({} as AssistantAnalytics);
  return {
    ...source,
    range: normaliseRange(source.range),
    metrics: normaliseMetrics(source.metrics),
    engagementRate: asNumber(source.engagementRate),
    postsPublished: asNumber(source.postsPublished),
    platformBreakdown: asDefinedArray(source.platformBreakdown).map((entry) => ({
      ...entry,
      metrics: normaliseMetrics(entry.metrics),
    })),
    changes: source.changes ?? {},
    headline: asString(source.headline),
    highlights: asArray(source.highlights),
    flags: asArray(source.flags),
  };
}

export const assistantApi = {
  async capabilities(): Promise<Capabilities> {
    const { data } = await api.get<Capabilities>('/assistant/capabilities');
    return normaliseCapabilities(data);
  },

  async variations(input: {
    topic: string;
    platforms: PlatformId[];
    tone?: Tone;
    goal?: string;
    language?: string;
  }): Promise<{ variants: VariationResult[] }> {
    const { data } = await api.post<{ variants: VariationResult[] }>('/assistant/variations', input);
    return { variants: asDefinedArray(data?.variants).map((variant) => normaliseVariant(variant)) };
  },

  async preflight(input: {
    platformIds: PlatformId[];
    scheduledAt: string;
    timezone?: string;
    content?: string;
  }): Promise<PreflightResult> {
    const { data } = await api.post<PreflightResult>('/assistant/preflight/schedule', input);
    return data;
  },

  /** Plans a campaign. Writes nothing — the response is a proposal. */
  async planCampaign(input: CampaignPlanInput): Promise<CampaignProposal> {
    const { data } = await api.post<CampaignProposal>('/assistant/campaigns/plan', input);
    return normaliseProposal(data);
  },

  /** Persists a proposal. The API rejects this unless `confirm: true`. */
  async createCampaign(
    input: CampaignPlanInput & { confirm: true }
  ): Promise<{ campaign: { id: string; name: string }; demo: boolean }> {
    const { data } = await api.post<{ campaign: { id: string; name: string }; demo: boolean }>(
      '/assistant/campaigns',
      input
    );
    return {
      campaign: {
        id: asString(data?.campaign?.id),
        name: asString(data?.campaign?.name),
      },
      demo: Boolean(data?.demo),
    };
  },

  async analyticsSummary(query: RangeQuery): Promise<AssistantAnalytics> {
    const { data } = await api.get<AssistantAnalytics>('/assistant/analytics/summary', { params: query });
    return normaliseAnalytics(data);
  },

  async topPosts(
    query: RangeQuery & { metric?: MetricKey; limit?: number }
  ): Promise<{ range: AssistantAnalytics['range']; metric: MetricKey; posts: RankedPost[] }> {
    const { data } = await api.get<{ range: AssistantAnalytics['range']; metric: MetricKey; posts: RankedPost[] }>(
      '/assistant/analytics/top-posts',
      { params: query }
    );
    return {
      range: normaliseRange(data?.range),
      metric: normaliseMetricKey(data?.metric),
      posts: asDefinedArray(data?.posts).map((post) => normaliseRankedPost(post)),
    };
  },

  async summarize(query: RangeQuery & { focus?: string }): Promise<AssistantNarrative> {
    const { data } = await api.post<AssistantNarrative>('/assistant/analytics/summarize', query);
    return normaliseNarrative(data);
  },
};

export const RANGE_OPTIONS: AssistantRange[] = ['last_7_days', 'last_30_days', 'this_week', 'last_week', 'today'];
export { METRIC_KEYS, PLATFORM_IDS, TONES };
export type { MetricKey, PlatformId, Tone };
export { apiErrorMessage };
